/**
 * @file task.tsx
 * @description Компонент страницы каталога задач для управления сущностями задач в системе управления 
 * задачами. Предоставляет комплексную функциональность управления задачами, включая создание, 
 * редактирование, фильтрацию и просмотр с уровнями приоритета, отслеживанием статуса и назначением 
 * ответственности. Включает многовкладочный интерфейс с основным обзором, связями предшественников, 
 * связанными багами, отслеживанием истории и интеграцией с коммитами. Включает продвинутую фильтрацию по 
 * сервису, статусу, приоритету и ответственному лицу для эффективной организации задач.
 * @author AI Assistant
 * @created 2025-09-04
 * @updated 2025-09-04
 * @dependencies ['@forms/task', '@utils/table-view', '@features/filter/types', '@forms/task/tabs', '@components/icons/priority-cell-header', 'react-dom/server']
 * @related-files ['@forms/task/index.tsx', '@forms/task/tabs/Main.tsx', '@forms/task/tabs/Predecessors.tsx', '@forms/task/tabs/History.tsx', '@forms/task/tabs/BugsTab.tsx', '@forms/task/tabs/CommitsTab.tsx']
 */

import { Form } from '@forms/task';
import { getDefaultCell, getDateTimeCell, getPriorityCell } from '@utils/table-view';
import { tabObject, TBaseObjectDto } from '@src/types';
import { filterComponent } from '@features/filter/types';
import { Main, Predecessors, History, BugsTab, CommitsTab } from '@forms/task/tabs';
import { PriorityCellHeader } from '@components/icons/priority-cell-header';
import { renderToStaticMarkup } from 'react-dom/server';

export const title = 'Задачи';
export const searchModalTitle = 'Выбор задачи';
export const type = 'Task';
export const path = 'task';
export const form = Form;
export const isCanAddGroups = true;
export const tabs: tabObject[] = [
    { label: 'Обзор', component: Main },
    { label: 'Предшественники', component: Predecessors, tab: 'predecessors' },
    { label: 'Ошибки', component: BugsTab, tab: 'bugs' },
    { label: 'История', component: History, tab: 'histories' },
    { label: 'Коммиты', component: CommitsTab, tab: 'commits' }
];

export const columns = [
    {
        id: '$item',
        header: [{ text: 'Код' }],
        width: 140,
        render: (cellValue: any) => getDefaultCell(cellValue)
    },
    {
        id: 'date',
        header: [{ text: 'Дата' }],
        width: 98,
        align: 'center',
        render: (cellValue: number) => getDateTimeCell(cellValue)
    },
    {
        id: 'state',
        header: [{ text: 'Статус' }],
        width: 143,
        render: (cellValue: any) => getDefaultCell(cellValue)
    },
    {
        id: 'priority',
        header: [{ text: renderToStaticMarkup(<PriorityCellHeader />), htmlEnable: true }],
        width: 32,
        align: 'center',
        render: (cellValue: any) => getPriorityCell(cellValue)
    },
    {
        id: 'name',
        header: [{ text: 'наименование' }],
        render: (cellValue: any) => getDefaultCell(cellValue)
    },
    {
        id: 'service',
        header: [{ text: 'Сервис' }],
        width: 190,
        render: (cellValue: any) => getDefaultCell(cellValue)
    },
    {
        id: 'executor',
        header: [{ text: 'Ответственный' }],
        width: 230,
        render: (cellValue: any) => getDefaultCell(cellValue)
    }
];

export const getFilterControls = (data: TBaseObjectDto = {}): filterComponent[] => [
    {
        filterType: 'dictionary',
        filterProps: {
            label: 'Сервис',
            type: 'Service'
        },
        value: data.service,
        name: 'service',
        tab: 'filters'
    },
    {
        filterType: 'select',
        filterProps: {
            label: 'Статус',
            emptyItem: 'Все'
        },
        value: data.state,
        type: 'ETaskState',
        name: 'state',
        tab: 'filters'
    },
    {
        filterType: 'select',
        filterProps: {
            label: 'Приоритет',
            emptyItem: 'Все'
        },
        type: 'ETaskPriority',
        value: data.priority,
        name: 'priority',
        tab: 'filters'
    },
    {
        filterType: 'dictionary',
        filterProps: {
            label: 'Ответственный',
            type: 'User'
        },
        value: data.executor,
        name: 'executor',
        tab: 'filters'
    }
];
